/**
 * What a COD buy-box tells the storefront's measurement beacon (feature 0037 lot 0).
 *
 * The one-page COD funnel HAS a basket — a variant, a quantity, and whichever quantity offer the shopper
 * picked. What it does not have is the `aqx.cart.v1` localStorage drawer the beacon watches, because nothing
 * is ever added to a cart: the selection lives in this page's React state until the order is placed. So the
 * merchant's Live view funnel reads `0 paniers → 0 checkouts → N achats` on a shop that sells perfectly well.
 * We were not measuring the absence of a basket; we were missing one that is right there.
 *
 * Three decisions shape this module:
 *
 *  1. **The event carries the lines itself.** Every other cart signal tells the beacon "something changed" and
 *     lets it read the storage. There is nothing to read here, and inventing a storage entry for a number on
 *     a dashboard would be writing to a shopper's device to measure them. So the announcement is the payload.
 *  2. **Ids and quantities, never a price.** Same contract as the storage cart: the server re-prices with the
 *     code that has authority at checkout, so this page has no way to state what its own basket is worth.
 *     That is the whole reason the payload is this narrow, and it is why the caps + the wire format live in
 *     the beacon (apps/storefront/src/lib/beacon.ts), not here.
 *  3. **A raw event name, not a shared import.** `ui-react` is consumed by the builder as well as the
 *     storefront and cannot import from `apps/storefront`. The string below is therefore duplicated there, by
 *     the same necessity that already duplicates {@link CHECKOUT_INTENT_EVENT}.
 *
 * Nothing here changes what a buyer sees or does: no DOM, no storage, no request on this side — a `dispatchEvent`
 * into a page where, in the builder, nobody is listening.
 */
import { useEffect, useState } from 'react';
/**
 * Mirrors `COD_CART_EVENT` in `apps/storefront/src/lib/beacon.ts`.
 *
 * Deliberately NOT `aqx:cart-changed`: that is the storage cart's own change signal, and the beacon answers it
 * by READING `aqx.cart.v1`. A COD page dispatching it would make the beacon read an empty drawer and report an
 * empty basket — the exact number this lot exists to fix, reported with more confidence.
 */
export const COD_CART_EVENT = 'aqx:cod-cart';
/**
 * Mirrors `CHECKOUT_INTENT_EVENT` in `apps/storefront/src/lib/beacon.ts` — the moment a shopper commits their
 * details. Both COD surfaces fire it, so the funnel's middle number means the same thing on both paradigms.
 */
export const CHECKOUT_INTENT_EVENT = 'aqx:checkout-intent';
/**
 * The DOM event a storefront form fires when the shopper has TYPED who they are (feature 0072 étape 4,
 * abandoned cart) — an e-mail and/or a phone, before any order exists. Fired on blur / after a pause, never
 * per keystroke, and only when a side looks complete. The storefront beacon listens and hands it to the
 * server with the open session; the journey engine is what may write to it later.
 */
export const CONTACT_EVENT = 'aqx:contact';
export function announceContact(contact) {
    if (typeof window === 'undefined')
        return;
    const email = contact.email?.trim() ?? '';
    const phone = contact.phone?.trim() ?? '';
    if (!(email.includes('@') && email.length > 4) && phone.replace(/\D/g, '').length < 6)
        return;
    try {
        window.dispatchEvent(new CustomEvent(CONTACT_EVENT, { detail: { ...contact, email, phone } }));
    }
    catch {
        // Measurement must never be able to break a sale.
    }
}
/**
 * Announce what the buy-box is holding. An EMPTY array is a real, meaningful announcement: it is what takes the
 * visitor back out of the funnel's cart step, exactly as an emptied storage cart does.
 */
export function announceCodCart(lines) {
    if (typeof window === 'undefined')
        return;
    try {
        window.dispatchEvent(new CustomEvent(COD_CART_EVENT, { detail: { lines } }));
    }
    catch {
        // Measurement must never be able to break a sale. A page with no CustomEvent constructor simply is not
        // counted — the same trade the beacon itself makes on every one of its own calls.
    }
}
/** Announce that the shopper committed their details (the funnel's checkout step). */
export function announceCheckoutIntent() {
    if (typeof window === 'undefined')
        return;
    try {
        window.dispatchEvent(new CustomEvent(CHECKOUT_INTENT_EVENT));
    }
    catch {
        // See above: never throw into a buyer's order.
    }
}
/**
 * Report the COD basket, from the buyer's FIRST DELIBERATE GESTURE onward.
 *
 * **Why not from the moment the page renders.** The quantity is 1 before the shopper has done anything. Signalling
 * on display would make "paniers actifs" a synonym for "a vu la page produit": the step would always equal the one
 * above it and would discriminate nothing. So the basket starts existing at the first gesture that is unmistakably
 * a choice — the quantity moved off what the page opened with (a stepper click, an offer tile, a volume tier: they
 * all write the same shared quantity), or a delivery field filled in. That is the COD equivalent of "added to cart".
 *
 * **Why the arming is sticky.** Once a shopper has chosen, going back to the starting quantity or clearing a field
 * does not un-choose: they are still standing in front of the buy-box holding something. Un-arming would make the
 * visitor blink in and out of the merchant's funnel while they made up their mind.
 *
 * Afterwards every change to the selection re-announces, so the reported line always describes what the shopper is
 * holding NOW — including the case where it describes nothing (no variant resolved), which reports an empty basket.
 *
 * SSR-safe by construction: it renders no DOM and does its work in effects, which never run on the server — so the
 * hydrated tree stays byte-identical to the SSR HTML.
 */
export function useCodCartBeacon({ active, variantId, quantity, formTouched, extraLines, lines, }) {
    // The quantity the buy-box OPENED with: 1, or the preselected offer tier's pack size (seeded at the provider,
    // identically on SSR and hydration). Captured on the first render and never revised — it is the baseline the
    // "quantity moved" gesture is measured against, not a live value.
    const [openedWithQuantity] = useState(quantity);
    const [armed, setArmed] = useState(false);
    const gesture = quantity !== openedWithQuantity || formTouched;
    const holding = active && (armed || gesture);
    useEffect(() => {
        if (holding && !armed)
            setArmed(true);
    }, [holding, armed]);
    // Re-announce whenever the extra lines change identity, not just when the array is a new object — the
    // block rebuilds them on every render, and keying the effect on the array itself would fire the beacon on
    // every keystroke in the delivery form.
    const extraKey = (extraLines ?? []).map((l) => `${l.variantId}:${l.qty}`).join('|');
    // Same for the per-unit lines: a mixed pack re-announces when a unit's variant changes, not on every render.
    const linesKey = (lines ?? []).map((l) => `${l.variantId}:${l.qty}`).join('|');
    useEffect(() => {
        if (!holding)
            return;
        // No resolvable variant → announce an EMPTY basket rather than a line the server cannot price. Reporting
        // nothing at all would leave the visitor parked in the cart step holding a basket that no longer exists.
        const main = lines && lines.length > 0
            ? lines.filter((l) => l.variantId && l.qty >= 1)
            : variantId && quantity >= 1
                ? [{ variantId, qty: quantity }]
                : [];
        const extra = (extraLines ?? []).filter((l) => l.variantId && l.qty >= 1);
        announceCodCart([...main, ...extra]);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- extraKey / linesKey are the stable identities of extraLines / lines
    }, [holding, variantId, quantity, extraKey, linesKey]);
}
/**
 * The hook as a render-nothing component, so a buy-box can mount it conditionally (COD mode only) without
 * making its own hook order conditional. Mount it at the buy-box ROOT rather than inside the form body: the
 * popup layout unmounts its body every time the modal closes, and an unmounted beacon would forget that this
 * shopper had already chosen.
 */
export function CodCartBeacon(props) {
    useCodCartBeacon(props);
    return null;
}
//# sourceMappingURL=codCartBeacon.js.map